/**
 * Every backend response is `{success, message, data}`. It is unwrapped once,
 * here, at the HTTP boundary — components receive `data` only and branch on
 * `error_code`, **never on `message`** (`api-and-data-contracts.md` §2.1).
 */

export interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
  error_code?: string;
  details?: ApiErrorDetail[];
}

export interface ApiErrorDetail {
  code: string;
  field?: string;
  message?: string;
}

/**
 * Reads a list payload however the answering service shaped it.
 *
 * **`api-and-data-contracts.md` §2 fixes the envelope, not the payload.** It says
 * an empty collection is `data: []` and that pagination metadata is part of the
 * contract — so a paginated route answers `data.items` and a whole-vocabulary
 * route answers `data` as a bare array, and both are correct. The nine deployed
 * services split exactly along that line, and this console reads both kinds.
 *
 * Reading only one of the two shapes is not a type error at the boundary — `data`
 * is whatever the service sent — it is a `TypeError` on `payload.items.map` deep
 * inside a component, which reaches the operator as the console's generic «حدث خلل
 * غير متوقّع» on a route whose data arrived perfectly intact. That is the failure
 * this replaces, and it is worse than a real error because there is nothing wrong.
 *
 * Anything that is neither shape reads as empty rather than throwing: a surface
 * with no rows states that it has none, which is true, and is what every list on
 * this console already draws when a service genuinely publishes nothing.
 */
export function asRows<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (typeof payload === 'object' && payload !== null) {
    const { items } = payload as { items?: unknown };
    if (Array.isArray(items)) return items as T[];
  }
  return [];
}

/** The single error type thrown at the client boundary. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly xRequestId: string | null;
  readonly details: ApiErrorDetail[] | undefined;
  readonly retryAfter: number | undefined;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    xRequestId: string | null;
    details?: ApiErrorDetail[];
    retryAfter?: number;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.xRequestId = init.xRequestId;
    this.details = init.details;
    this.retryAfter = init.retryAfter;
  }
}

/** Raised before a response exists — DNS, offline, a dropped connection. */
export class NetworkError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'NetworkError';
  }
}

const UNKNOWN_ERROR_CODE = 'UNKNOWN';

function isEnvelope(value: unknown): value is ApiEnvelope<unknown> {
  return typeof value === 'object' && value !== null && 'success' in value && 'data' in value;
}

/**
 * `X-Request-ID` is generated by nginx and by nothing else. It is read off
 * every response so an error surface can offer it as a support reference.
 */
export function readRequestId(response: Response): string | null {
  const header = response.headers.get('X-Request-ID') ?? response.headers.get('x-request-id');
  if (header === null) return null;

  // **The edge and the service both set this header**, so the response carries
  // two `X-Request-ID` lines with the same value and `Headers.get` returns them
  // joined — `"…e557fb, …e557fb"`. That reached the error card verbatim and an
  // operator quoted a doubled reference to support.
  //
  // The first value wins because nginx's is the outermost and the one its access
  // log carries. Splitting rather than de-duplicating on purpose: two *different*
  // values would mean the request crossed a hop that reassigned it, and quoting
  // the outer one is still right.
  const [first] = header.split(',');
  return first?.trim() || null;
}

function readRetryAfter(response: Response, body: unknown): number | undefined {
  const fromBody =
    isEnvelope(body) && typeof body.data === 'object' && body.data !== null && 'retry_after' in body.data
      ? Number((body.data as { retry_after: unknown }).retry_after)
      : Number.NaN;

  if (Number.isFinite(fromBody)) return fromBody;

  const header = Number(response.headers.get('Retry-After'));
  return Number.isFinite(header) ? header : undefined;
}

/**
 * Unwraps one response. Throws `ApiError` on any non-2xx **and** on a 2xx
 * carrying `success: false` — a `200` with `success: false` is a contract
 * violation on the backend's side, not a success on ours.
 */
export async function parseEnvelope<T>(response: Response): Promise<T> {
  const xRequestId = readRequestId(response);

  let body: unknown = null;
  const raw = await response.text();
  if (raw.length > 0) {
    try {
      body = JSON.parse(raw) as unknown;
    } catch {
      body = null;
    }
  }

  if (!isEnvelope(body)) {
    throw new ApiError({
      status: response.status,
      code: UNKNOWN_ERROR_CODE,
      message: 'استجابة غير متوقّعة من الخادم.',
      xRequestId,
    });
  }

  if (!response.ok || !body.success) {
    const errorDetails = detailsOf(body);
    throw new ApiError({
      status: response.status,
      code: body.error_code ?? UNKNOWN_ERROR_CODE,
      message: body.message,
      xRequestId,
      // **Either shape.** The platform answers a refusal two ways: `VALIDATION_FAILED`
      // puts `details` at the top level, while a domain refusal such as
      // `CONTENT_REQUIRED_METADATA_MISSING` nests it under `data`. Reading only the
      // top level meant the publish refusal — which does name its outstanding fields —
      // arrived here with none, and the console rendered «required data missing» with
      // nothing to fix.
      ...(errorDetails !== undefined ? { details: errorDetails } : {}),
      ...(readRetryAfter(response, body) !== undefined
        ? { retryAfter: readRetryAfter(response, body) as number }
        : {}),
    });
  }

  return body.data as T;
}

/** The `details[]` of a refusal, wherever this service put it. */
function detailsOf(body: { details?: unknown; data?: unknown }): ApiErrorDetail[] | undefined {
  if (Array.isArray(body.details)) return body.details as ApiErrorDetail[];
  const nested = (body.data as { details?: unknown } | null | undefined)?.details;
  return Array.isArray(nested) ? (nested as ApiErrorDetail[]) : undefined;
}
